import { clamp } from '../lib/math';
import { fovDeg, panel, selection, viewMode } from '../state';
import type { SkyEngine } from './engine';

export const FOV_MIN = 0.05; // 3 arcminutes: enough to see Saturn's rings and Jupiter's belts
export const FOV_MAX = 150;

/** Touch, mouse and wheel gestures for panning, zooming and selecting. */
export function attachControls(engine: SkyEngine, el: HTMLElement, hooks: { onTap: (x: number, y: number) => void; onPan: () => void }) {
  const pts = new Map<number, { x: number; y: number }>();
  let startX = 0, startY = 0, startT = 0, moved = false;
  let lastX = 0, lastY = 0, lastMoveT = 0;
  let pinchDist = 0, pinchFov = 0;
  let lastTapT = 0;

  const degPerPx = () => fovDeg.peek() / Math.min(engine.camera.width, engine.camera.height);

  el.addEventListener('pointerdown', (e) => {
    el.setPointerCapture(e.pointerId);
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    engine.cancelAnimation();
    if (pts.size === 1) {
      startX = lastX = e.clientX;
      startY = lastY = e.clientY;
      startT = lastMoveT = performance.now();
      moved = false;
      engine.interacting = true;
      engine.velAz = engine.velAlt = 0;
    } else if (pts.size === 2) {
      const [a, b] = [...pts.values()];
      pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
      pinchFov = fovDeg.peek();
      moved = true;
    }
  });

  el.addEventListener('pointermove', (e) => {
    if (!pts.has(e.pointerId)) return;
    pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pts.size === 2) {
      const [a, b] = [...pts.values()];
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchDist > 0) fovDeg.value = clamp((pinchFov * pinchDist) / d, FOV_MIN, FOV_MAX);
      return;
    }
    const dx = e.clientX - lastX, dy = e.clientY - lastY;
    if (!moved && Math.hypot(e.clientX - startX, e.clientY - startY) > 7) {
      moved = true;
      // While pointing, this hands the view to the finger (synchronously, so the drag carries on from it).
      hooks.onPan();
    }
    if (!moved) return;
    const k = degPerPx() * (engine.camera.projection === 'stereographic' ? 1.0 : 1.0);
    const now = performance.now();
    const dt = Math.max(1, now - lastMoveT) / 1000;
    const dAz = -dx * k, dAlt = dy * k;
    engine.viewAz += dAz;
    engine.viewAlt = clamp(engine.viewAlt + dAlt, -89.5, 89.5);
    engine.velAz = engine.velAz * 0.6 + (dAz / dt) * 0.4;
    engine.velAlt = engine.velAlt * 0.6 + (dAlt / dt) * 0.4;
    lastX = e.clientX;
    lastY = e.clientY;
    lastMoveT = now;
  });

  const end = (e: PointerEvent) => {
    if (!pts.has(e.pointerId)) return;
    pts.delete(e.pointerId);
    if (pts.size === 1) {
      const [p] = [...pts.values()];
      lastX = p.x;
      lastY = p.y;
      lastMoveT = performance.now();
      return;
    }
    if (pts.size > 0) return;
    engine.interacting = false;
    if (performance.now() - lastMoveT > 90) engine.velAz = engine.velAlt = 0;
    const tapped = !moved && performance.now() - startT < 400;
    if (tapped) {
      const now = performance.now();
      if (now - lastTapT < 300 && viewMode.peek() === 'manual') {
        // Double tap: zoom in towards the point.
        const dir = engine.camera.unproject(e.clientX, e.clientY);
        engine.flyTo(dir, clamp(fovDeg.peek() / 2.2, FOV_MIN, FOV_MAX), 0.5);
        lastTapT = 0;
        return;
      }
      lastTapT = now;
      hooks.onTap(e.clientX, e.clientY);
    }
  };
  el.addEventListener('pointerup', end);
  el.addEventListener('pointercancel', end);
  // Capture lost without either (rare): let go too, so no finger is left down for ever.
  el.addEventListener('lostpointercapture', end);

  el.addEventListener(
    'wheel',
    (e) => {
      e.preventDefault();
      engine.cancelAnimation();
      const f = Math.exp(e.deltaY * (e.ctrlKey ? 0.01 : 0.0015));
      fovDeg.value = clamp(fovDeg.peek() * f, FOV_MIN, FOV_MAX);
    },
    { passive: false },
  );

  // Keyboard (desktop): arrows pan, +/- zoom, Esc clears the selection.
  window.addEventListener('keydown', (e) => {
    if ((e.target as HTMLElement)?.closest?.('input, textarea')) return;
    // Behind anything modal (a sheet, the welcome, a film, "Show me around", which steps with the arrows), the keys
    // belong to it, not to the sky.
    if (panel.peek() === 'walkthrough' || document.querySelector('[aria-modal="true"]')) return;
    const step = fovDeg.peek() / 12;
    if (e.key === 'ArrowLeft') engine.viewAz -= step;
    else if (e.key === 'ArrowRight') engine.viewAz += step;
    else if (e.key === 'ArrowUp') engine.viewAlt = clamp(engine.viewAlt + step, -89.5, 89.5);
    else if (e.key === 'ArrowDown') engine.viewAlt = clamp(engine.viewAlt - step, -89.5, 89.5);
    else if (e.key === '+' || e.key === '=') fovDeg.value = clamp(fovDeg.peek() / 1.25, FOV_MIN, FOV_MAX);
    else if (e.key === '-') fovDeg.value = clamp(fovDeg.peek() * 1.25, FOV_MIN, FOV_MAX);
    else if (e.key === 'Escape' && !panel.peek()) selection.value = null;
  });
}
